import type {
  IntrospectionTypeRef,
  IntrospectionType,
  IntrospectionSchema,
} from "../../types/introspection";

// ── Type ref formatting ─────────────────────────────────────────────

export function formatTypeRef(ref: IntrospectionTypeRef): string {
  if (ref.kind === "NON_NULL") return `${formatTypeRef(ref.ofType!)}!`;
  if (ref.kind === "LIST") return `[${formatTypeRef(ref.ofType!)}]`;
  return ref.name ?? "?";
}

/** Unwrap NON_NULL / LIST wrappers to get the named type */
export function unwrapTypeRef(ref: IntrospectionTypeRef): IntrospectionTypeRef {
  let current = ref;
  while (current.ofType && (current.kind === "NON_NULL" || current.kind === "LIST")) {
    current = current.ofType;
  }
  return current;
}

// ── Color coding per type kind ──────────────────────────────────────

export function kindColor(kind: string, isRoot = false): string {
  if (isRoot) return "text-schema-root";
  switch (kind) {
    case "OBJECT":
      return "text-zinc-300";
    case "ENUM":
      return "text-schema-enum";
    case "INPUT_OBJECT":
      return "text-schema-input";
    case "INTERFACE":
    case "UNION":
      return "text-schema-abstract";
    case "SCALAR":
      return "text-schema-scalar";
    default:
      return "text-zinc-400";
  }
}

export function kindBadgeColor(kind: string): string {
  switch (kind) {
    case "OBJECT":
      return "bg-zinc-700/50 text-zinc-400";
    case "ENUM":
      return "bg-schema-enum-bg text-schema-enum";
    case "INPUT_OBJECT":
      return "bg-schema-input-bg text-schema-input";
    case "INTERFACE":
    case "UNION":
      return "bg-schema-abstract-bg text-schema-abstract";
    case "SCALAR":
      return "bg-schema-scalar-bg text-schema-scalar";
    default:
      return "bg-zinc-700/50 text-zinc-500";
  }
}

// ── Schema parsing ──────────────────────────────────────────────────

export function parseSchema(json: string): IntrospectionSchema | null {
  try {
    const parsed = JSON.parse(json);
    return parsed?.data?.__schema ?? parsed?.__schema ?? null;
  } catch {
    return null;
  }
}

export function parseSchemaTypes(json: string): IntrospectionType[] {
  const schema = parseSchema(json);
  if (!schema?.types) return [];
  return schema.types.filter((t) => !t.name.startsWith("__"));
}

export function buildTypeMap(types: IntrospectionType[]): Map<string, IntrospectionType> {
  const map = new Map<string, IntrospectionType>();
  for (const t of types) map.set(t.name, t);
  return map;
}

export function getRootTypeNames(json: string): Set<string> {
  const schema = parseSchema(json);
  if (!schema) return new Set();
  const names = new Set<string>();
  if (schema.queryType?.name) names.add(schema.queryType.name);
  if (schema.mutationType?.name) names.add(schema.mutationType.name);
  if (schema.subscriptionType?.name) names.add(schema.subscriptionType.name);
  return names;
}
